import { supabase } from '@/integrations/supabase/client';
import { EQUIPMENT_FLAT_LIST } from '@/lib/equipmentTypes';
import { applyDemoFilter } from '@/lib/demoFilter';

/**
 * Radius clamp helpers — single source of truth.
 * Minimum allowed DH-O / DH-D radius is 1. Maximum is 475.
 * Blank / null / undefined / non-numeric → fallback (per-surface default).
 * 0 and negatives → 1.
 */
export const RADIUS_MIN = 1;
export const RADIUS_MAX = 475;

export function clampRadiusValue(value: unknown, fallback: number): number {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string' && value.trim() === '') return fallback;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  if (!Number.isFinite(n)) return fallback;
  if (n < RADIUS_MIN) return RADIUS_MIN;
  if (n > RADIUS_MAX) return RADIUS_MAX;
  return Math.floor(n);
}

export function clampRadiusString(value: unknown, fallback: number): string {
  return String(clampRadiusValue(value, fallback));
}

/** Set of all equipment enum values known to the DB (derived from the canonical flat list). */
const DB_EQUIPMENT_ENUM = new Set(EQUIPMENT_FLAT_LIST.map(e => e.enumValue));

/**
 * Explicit equipment family mapping.
 * When a user selects a base type (van, reefer, flatbed), the search expands
 * to include all intentionally-listed subtypes. Cross-family types like
 * van_or_flatbed are deliberately assigned to the families where they belong.
 */
export const EQUIPMENT_FAMILIES: Record<string, string[]> = {
  van: [
    'van', 'van_air_ride', 'van_conestoga', 'van_double', 'van_hazmat',
    'van_hotshot', 'van_insulated', 'van_intermodal', 'van_lift_gate',
    'van_logistics', 'van_open_top', 'van_roller_bed', 'van_triple',
    'van_vented', 'van_blanket_wrap', 'van_with_curtains', 'van_pallet_exchange',
    'van_with_team',
    // Cross-family: loads that accept a van
    'van_or_flatbed', 'van_or_flatbed_tarps', 'van_or_reefer',
    'insulated_van_or_reefer', 'reefer_or_vented_van', 'flatbed_van_reefer',
  ],
  flatbed: [
    'flatbed', 'flatbed_air_ride', 'flatbed_conestoga', 'flatbed_double',
    'flatbed_hazmat', 'flatbed_hotshot', 'flatbed_maxi', 'flatbed_or_step_deck',
    'flatbed_over_dimension', 'flatbed_with_chains', 'flatbed_with_sides',
    'flatbed_with_tarps', 'flatbed_with_team', 'flatbed_pallet_exchange',
    // Cross-family: loads that accept a flatbed
    'van_or_flatbed', 'van_or_flatbed_tarps', 'flatbed_van_reefer',
  ],
  reefer: [
    'reefer', 'reefer_air_ride', 'reefer_double', 'reefer_hazmat',
    'reefer_intermodal', 'reefer_logistics', 'reefer_with_team',
    'reefer_pallet_exchange',
    // Cross-family: loads that accept a reefer
    'reefer_or_vented_van', 'van_or_reefer', 'insulated_van_or_reefer',
    'flatbed_van_reefer',
  ],
};

/**
 * Combo enums — broad multi-family OR (NOT exact-only).
 * Selecting one of these expands to the union of every referenced family.
 */
export const EQUIPMENT_COMBOS = new Set<string>([
  'van_or_reefer',
  'van_or_flatbed',
  'van_or_flatbed_tarps',
  'insulated_van_or_reefer',
  'reefer_or_vented_van',
  'flatbed_van_reefer',
]);

/** Reverse-lookup: enum → parent family keys (subtype fallback). */
function getParentFamilies(enumValue: string): string[] {
  const parents: string[] = [];
  for (const [family, members] of Object.entries(EQUIPMENT_FAMILIES)) {
    if (members.includes(enumValue)) parents.push(family);
  }
  return parents;
}

/**
 * Primary expansion (used for the main results query):
 *   - main family (van/reefer/flatbed) → full family set
 *   - combo (van_or_reefer, …)         → union of every referenced family
 *   - specific subtype                  → exact [enum] only
 */
export function expandEquipmentTypes(selected: string[]): string[] {
  const expanded = new Set<string>();
  for (const eq of selected) {
    if (EQUIPMENT_FAMILIES[eq]) {
      for (const m of EQUIPMENT_FAMILIES[eq]) expanded.add(m);
    } else if (EQUIPMENT_COMBOS.has(eq)) {
      // Broad OR: every family this combo touches
      for (const [, members] of Object.entries(EQUIPMENT_FAMILIES)) {
        if (members.includes(eq)) {
          for (const m of members) expanded.add(m);
        }
      }
      expanded.add(eq); // safety: include combo enum itself
    } else {
      expanded.add(eq); // specific subtype: exact only
    }
  }
  return Array.from(expanded);
}

/**
 * Fallback expansion — only used when primary returned 0 results AND the
 * user's selection contains a specific subtype. Promotes each subtype to
 * its parent family; families and combos are unchanged.
 */
export function expandEquipmentTypesForFallback(selected: string[]): string[] {
  const expanded = new Set<string>();
  for (const eq of selected) {
    if (EQUIPMENT_FAMILIES[eq] || EQUIPMENT_COMBOS.has(eq)) {
      for (const m of expandEquipmentTypes([eq])) expanded.add(m);
      continue;
    }
    const parents = getParentFamilies(eq);
    if (parents.length === 0) {
      expanded.add(eq);
    } else {
      for (const family of parents) {
        for (const m of EQUIPMENT_FAMILIES[family]) expanded.add(m);
      }
    }
  }
  return Array.from(expanded);
}

/** True if the selection contains at least one specific subtype (not a family or combo). */
export function hasSpecificSubtype(selected: string[]): boolean {
  return selected.some(eq => !EQUIPMENT_FAMILIES[eq] && !EQUIPMENT_COMBOS.has(eq));
}

export interface LoadSearchParams {
  origin: string;
  destination: string;
  equipmentTypes: string[];
  loadType: 'full' | 'partial' | 'both';
  hideCanada: boolean;
  searchBack: number;
  dhoRadius?: number;
  dhdRadius?: number;
}

export interface DatabaseLoad {
  id: string;
  load_number: string;
  origin_city: string;
  origin_state: string;
  origin_country: string;
  origin_latitude?: number;
  origin_longitude?: number;
  destination_city: string;
  destination_state: string;
  destination_country: string;
  destination_latitude?: number;
  destination_longitude?: number;
  equipment_type: string;
  weight_lbs: number;
  length_ft: number;
  pickup_date: string;
  delivery_date: string;
  rate_amount: number;
  total_miles: number;
  status: string;
  commodity: string;
  special_instructions: string;
  is_public: boolean;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  posted_at: string;
  source: string | null;
  company_id: string | null;
  created_by: string | null;
  load_type: string | null;
  rate_type: string | null;
  reference_number: string | null;
  payment_terms: string | null;
  pieces: number | null;
  fuel_surcharge_rate: number | null;
  accessorial_charges: number | null;
  pickup_time_start: string | null;
  pickup_time_end: string | null;
  delivery_time_start: string | null;
  delivery_time_end: string | null;
  contact_phone_secondary: string | null;
  brief_audio_path?: string | null;
  brief_mime_type?: string | null;
  brief_duration_seconds?: number | null;
  companies?: { name: string } | null;
}

/**
 * Module-scoped, session-lifetime geocode cache.
 * Key: normalized (lowercased + trimmed + collapsed-whitespace) query string.
 * Value: { lat, lon } on hit, or null for a known miss (negative cached so
 * malformed/typo'd inputs don't keep retrying Mapbox on every keystroke-search).
 */
const geocodeMemoryCache = new Map<string, { lat: number; lon: number } | null>();

function normalizeGeocodeKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Resolve a location string to coordinates using a local-first cascade:
 *   1. In-memory session cache (instant)
 *   2. Local `search_locations` RPC against the 98k-cities DB (fast, no Mapbox)
 *   3. Mapbox `geocoding` edge function (fallback only)
 *
 * Negative results (null) are cached too. The edge function itself is unchanged.
 */
export async function geocodeLocation(location: string): Promise<{ lat: number; lon: number } | null> {
  try {
    if (!location || !location.trim()) return null;
    const key = normalizeGeocodeKey(location);

    // 1. In-memory session cache (hit returns immediately, including null misses)
    if (geocodeMemoryCache.has(key)) {
      return geocodeMemoryCache.get(key) ?? null;
    }

    // 2. Local DB cascade via search_locations RPC (same source used by
    //    bulkUploadSchema.ts, location-autocomplete, ShipmentDetailsCard, etc.)
    try {
      const { data: localResults } = await supabase.rpc('search_locations', { q: location, lim: 1 });
      if (localResults && localResults.length > 0) {
        const hit = localResults[0] as { lat: number | null; lon: number | null };
        if (hit.lat != null && hit.lon != null) {
          const coords = { lat: hit.lat, lon: hit.lon };
          geocodeMemoryCache.set(key, coords);
          return coords;
        }
      }
    } catch (rpcErr) {
      // Non-fatal: fall through to Mapbox
      console.warn('search_locations RPC failed, falling back to Mapbox:', rpcErr);
    }

    // 3. Mapbox fallback (edge function unchanged)
    const { data, error } = await supabase.functions.invoke('geocoding', {
      body: { query: location }
    });

    if (error || !data?.features?.length) {
      if (error) console.error('Geocoding error:', error);
      geocodeMemoryCache.set(key, null); // negative cache
      return null;
    }

    const [lon, lat] = data.features[0].center;
    const coords = { lat, lon };
    geocodeMemoryCache.set(key, coords);
    return coords;
  } catch (error) {
    console.error('Error geocoding location:', error);
    return null;
  }
}

// Compute a lat/lon bounding box from a center point and radius in miles
export function boundingBox(lat: number, lon: number, radiusMiles: number) {
  const latDelta = radiusMiles / 69;
  const lonDelta = radiusMiles / (69 * Math.cos(lat * Math.PI / 180));
  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLon: lon - lonDelta,
    maxLon: lon + lonDelta,
  };
}

// Haversine distance in miles between two points
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Filter loads by distance from a point
function filterByRadius(
  loads: DatabaseLoad[],
  centerLat: number,
  centerLon: number,
  radiusMiles: number,
  checkOrigin: boolean
): DatabaseLoad[] {
  if (radiusMiles === 0) {
    // 0 radius means exact match only (already filtered by city name)
    return loads;
  }

  return loads.filter(load => {
    const lat = checkOrigin ? load.origin_latitude : load.destination_latitude;
    const lon = checkOrigin ? load.origin_longitude : load.destination_longitude;
    
    if (!lat || !lon) return false;

    return haversineDistance(centerLat, centerLon, lat, lon) <= radiusMiles;
  });
}

export const searchLoads = async (
  params: LoadSearchParams,
  preResolvedOriginCoords?: GeoCoords | null,
  preResolvedDestCoords?: GeoCoords | null,
  isDemoUser: boolean = false,
): Promise<DatabaseLoad[]> => {
  try {
    // Use pre-resolved coords if provided, otherwise geocode (backward compat)
    const originCoords = preResolvedOriginCoords !== undefined
      ? preResolvedOriginCoords
      : (params.origin ? await geocodeLocation(params.origin) : null);
    const destCoords = preResolvedDestCoords !== undefined
      ? preResolvedDestCoords
      : (params.destination ? await geocodeLocation(params.destination) : null);

    // Expiration cutoff: only loads with pickup_date >= today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expirationCutoff = today.toISOString().split('T')[0];

    // --- Single query with all DB-level filters ---
    let query = supabase
      .from('loads')
      .select('*, companies(name, documents_verified_at)')
      .eq('is_public', true)
      .eq('status', 'posted')
      .gte('pickup_date', expirationCutoff);

    // Demo isolation filter
    query = applyDemoFilter(query, isDemoUser);

    // Equipment type filter (with family expansion for base types)
    if (params.equipmentTypes && params.equipmentTypes.length > 0) {
      const expanded = expandEquipmentTypes(params.equipmentTypes.map(e => e.toLowerCase()));
      const validEquipment = expanded.filter(e => DB_EQUIPMENT_ENUM.has(e));
      if (validEquipment.length > 0) {
        query = query.in('equipment_type', validEquipment as any);
      }
    }

    // Load type filter
    if (params.loadType === 'full') {
      query = query.eq('load_type', 'FTL');
    } else if (params.loadType === 'partial') {
      query = query.in('load_type', ['LTL', 'Partial'] as any);
    }
    // 'both' → no load_type filter

    // Hide Canada filter
    if (params.hideCanada) {
      query = query.neq('origin_country', 'Canada').neq('destination_country', 'Canada');
    }

    // --- Origin: bounding box (radius>0 + coords) OR city ilike (radius=0) ---
    const originHasRadius = params.dhoRadius && params.dhoRadius > 0;
    if (params.origin && params.origin.trim()) {
      if (originHasRadius && originCoords) {
        const bb = boundingBox(originCoords.lat, originCoords.lon, params.dhoRadius!);
        query = query
          .gte('origin_latitude', bb.minLat)
          .lte('origin_latitude', bb.maxLat)
          .gte('origin_longitude', bb.minLon)
          .lte('origin_longitude', bb.maxLon);
      } else if (!originHasRadius) {
        const originCity = params.origin.split(',')[0].trim();
        query = query.ilike('origin_city', `%${originCity}%`);
      }
      // else: radius>0 but geocoding failed → skip prefilter gracefully
    }

    // --- Destination: bounding box (radius>0 + coords) OR city ilike (radius=0) ---
    const destHasRadius = params.dhdRadius && params.dhdRadius > 0;
    if (params.destination && params.destination.trim()) {
      if (destHasRadius && destCoords) {
        const bb = boundingBox(destCoords.lat, destCoords.lon, params.dhdRadius!);
        query = query
          .gte('destination_latitude', bb.minLat)
          .lte('destination_latitude', bb.maxLat)
          .gte('destination_longitude', bb.minLon)
          .lte('destination_longitude', bb.maxLon);
      } else if (!destHasRadius) {
        const destCity = params.destination.split(',')[0].trim();
        query = query.ilike('destination_city', `%${destCity}%`);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error searching loads:', error);
      throw error;
    }

    let results = (data || []) as DatabaseLoad[];

    // --- Precise Haversine pass on the narrowed set ---
    if (originCoords && originHasRadius) {
      results = filterByRadius(results, originCoords.lat, originCoords.lon, params.dhoRadius!, true);
    }

    if (destCoords && destHasRadius) {
      results = filterByRadius(results, destCoords.lat, destCoords.lon, params.dhdRadius!, false);
    }

    return results;
  } catch (error) {
    console.error('Error in searchLoads:', error);
    return [];
  }
};

/* ─── Annular ring filter: keeps loads where innerRadius < distance ≤ outerRadius ─── */
function filterByAnnulus(
  loads: DatabaseLoad[],
  centerLat: number,
  centerLon: number,
  innerRadius: number,
  outerRadius: number,
  checkOrigin: boolean
): DatabaseLoad[] {
  return loads.filter(load => {
    const lat = checkOrigin ? load.origin_latitude : load.destination_latitude;
    const lon = checkOrigin ? load.origin_longitude : load.destination_longitude;
    if (!lat || !lon) return false;
    const dist = haversineDistance(centerLat, centerLon, lat, lon);
    return dist > innerRadius && dist <= outerRadius;
  });
}

/**
 * Search for similar loads in an annular ring just outside the exact search radii.
 * Uses the same equipment/loadType/hideCanada/date filters — only the geographic
 * boundary differs: loads between (exactRadius, outerRadius].
 */
export const searchSimilarLoads = async (
  params: LoadSearchParams,
  originCoords: GeoCoords | null,
  destCoords: GeoCoords | null,
  exactIds: Set<string>,
  isDemoUser: boolean = false,
): Promise<DatabaseLoad[]> => {
  try {
    // Determine inner (exact) and outer radii for origin
    const exactDhoRadius = params.dhoRadius ?? 0;
    const innerOrigin = exactDhoRadius;
    const outerOrigin = exactDhoRadius > 0 ? Math.min(exactDhoRadius * 2, 500) : 100;

    // Determine inner (exact) and outer radii for destination
    const exactDhdRadius = params.dhdRadius ?? 0;
    const innerDest = exactDhdRadius;
    const outerDest = exactDhdRadius > 0 ? Math.min(exactDhdRadius * 2, 500) : 100;

    // Need at least one geocoded axis to run a similar search
    const hasOriginRing = !!originCoords && (params.origin?.trim() ?? '') !== '';
    const hasDestRing = !!destCoords && (params.destination?.trim() ?? '') !== '';
    if (!hasOriginRing && !hasDestRing) return [];

    // Expiration cutoff
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expirationCutoff = today.toISOString().split('T')[0];

    let query = supabase
      .from('loads')
      .select('*, companies(name, documents_verified_at)')
      .eq('is_public', true)
      .eq('status', 'posted')
      .gte('pickup_date', expirationCutoff);

    // Demo isolation filter
    query = applyDemoFilter(query, isDemoUser);

    // Same equipment filter (with family expansion for base types)
    if (params.equipmentTypes && params.equipmentTypes.length > 0) {
      const expanded = expandEquipmentTypes(params.equipmentTypes.map(e => e.toLowerCase()));
      const validEquipment = expanded.filter(e => DB_EQUIPMENT_ENUM.has(e));
      if (validEquipment.length > 0) {
        query = query.in('equipment_type', validEquipment as any);
      }
    }

    // Same load type filter
    if (params.loadType === 'full') {
      query = query.eq('load_type', 'FTL');
    } else if (params.loadType === 'partial') {
      query = query.in('load_type', ['LTL', 'Partial'] as any);
    }
    // 'both' → no load_type filter

    // Same hide Canada filter
    if (params.hideCanada) {
      query = query.neq('origin_country', 'Canada').neq('destination_country', 'Canada');
    }

    // Origin bounding box using OUTER radius
    if (hasOriginRing && originCoords) {
      const bb = boundingBox(originCoords.lat, originCoords.lon, outerOrigin);
      query = query
        .gte('origin_latitude', bb.minLat)
        .lte('origin_latitude', bb.maxLat)
        .gte('origin_longitude', bb.minLon)
        .lte('origin_longitude', bb.maxLon);
    }

    // Destination bounding box using OUTER radius
    if (hasDestRing && destCoords) {
      const bb = boundingBox(destCoords.lat, destCoords.lon, outerDest);
      query = query
        .gte('destination_latitude', bb.minLat)
        .lte('destination_latitude', bb.maxLat)
        .gte('destination_longitude', bb.minLon)
        .lte('destination_longitude', bb.maxLon);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error searching similar loads:', error);
      return [];
    }

    let results = (data || []) as DatabaseLoad[];

    // Annular ring filter: keep only loads OUTSIDE the exact radius
    if (hasOriginRing && originCoords) {
      results = filterByAnnulus(results, originCoords.lat, originCoords.lon, innerOrigin, outerOrigin, true);
    }
    if (hasDestRing && destCoords) {
      results = filterByAnnulus(results, destCoords.lat, destCoords.lon, innerDest, outerDest, false);
    }

    // Exclude exact result IDs
    results = results.filter(l => !exactIds.has(l.id));

    return results;
  } catch (error) {
    console.error('Error in searchSimilarLoads:', error);
    return [];
  }
};

export interface GeoCoords {
  lat: number;
  lon: number;
}

/**
 * Pure client-side check: does a single load match the current search filters?
 * Used by the realtime hook to decide if an INSERT should appear in results.
 */
/* ─────────────────────────────────────────────────────────────────
 * State-to-state fallback layers (Layer 2 + Layer 3) for Similar Results.
 * Pure additions — do not modify or replace searchLoads / searchSimilarLoads.
 * ────────────────────────────────────────────────────────────────── */

/** US 50 states + DC. CA provinces intentionally excluded (hideCanada filter handles CA). */
const US_STATE_CODES = new Set<string>([
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
]);

/**
 * Best-effort: derive a US state code from typed input.
 *   "Chicago, IL"      → "IL"
 *   "Houston, TX, USA" → "TX"
 *   "Atlanta GA"       → "GA"
 *   "Bronx"            → null
 * Pure string parsing (no DB / network). Returns null if not confidently parseable.
 */
export function parseStateFromInput(input: string | undefined | null): string | null {
  if (!input) return null;
  const cleaned = input.trim();
  if (!cleaned) return null;

  // Right-to-left token scan over comma- and whitespace-split tokens.
  const tokens = cleaned
    .split(/[,\s]+/)
    .map(t => t.trim())
    .filter(Boolean);

  for (let i = tokens.length - 1; i >= 0; i--) {
    const tok = tokens[i].toUpperCase().replace(/[^A-Z]/g, '');
    if (tok.length === 2 && US_STATE_CODES.has(tok)) return tok;
  }
  return null;
}

/**
 * LAYER 2 — state-to-state, same equipment family.
 * No radius, no city. Same DB filters as searchLoads, just origin_state / destination_state.
 */
export const searchStateToStateSameEquipment = async (
  params: LoadSearchParams,
  originStateCode: string,
  destStateCode: string,
  excludeIds: Set<string>,
  isDemoUser: boolean = false,
): Promise<DatabaseLoad[]> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expirationCutoff = today.toISOString().split('T')[0];

    let query = supabase
      .from('loads')
      .select('*, companies(name, documents_verified_at)')
      .eq('is_public', true)
      .eq('status', 'posted')
      .gte('pickup_date', expirationCutoff);

    query = applyDemoFilter(query, isDemoUser);

    if (params.equipmentTypes && params.equipmentTypes.length > 0) {
      const expanded = expandEquipmentTypes(params.equipmentTypes.map(e => e.toLowerCase()));
      const validEquipment = expanded.filter(e => DB_EQUIPMENT_ENUM.has(e));
      if (validEquipment.length > 0) {
        query = query.in('equipment_type', validEquipment as any);
      }
    }

    if (params.loadType === 'full') {
      query = query.eq('load_type', 'FTL');
    } else if (params.loadType === 'partial') {
      query = query.in('load_type', ['LTL', 'Partial'] as any);
    }

    if (params.hideCanada) {
      query = query.neq('origin_country', 'Canada').neq('destination_country', 'Canada');
    }

    query = query
      .eq('origin_state', originStateCode)
      .eq('destination_state', destStateCode);

    const { data, error } = await query;
    if (error) {
      console.error('Error in searchStateToStateSameEquipment:', error);
      return [];
    }

    const results = (data || []) as DatabaseLoad[];
    const filtered = results.filter(l => !excludeIds.has(l.id));
    return filtered;
  } catch (error) {
    console.error('Error in searchStateToStateSameEquipment:', error);
    return [];
  }
};

/**
 * LAYER 3 — state-to-state, ANY equipment.
 * Same as Layer 2 but the equipment filter is omitted entirely.
 */
export const searchStateToStateAnyEquipment = async (
  params: LoadSearchParams,
  originStateCode: string,
  destStateCode: string,
  excludeIds: Set<string>,
  isDemoUser: boolean = false,
): Promise<DatabaseLoad[]> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expirationCutoff = today.toISOString().split('T')[0];

    let query = supabase
      .from('loads')
      .select('*, companies(name, documents_verified_at)')
      .eq('is_public', true)
      .eq('status', 'posted')
      .gte('pickup_date', expirationCutoff);

    query = applyDemoFilter(query, isDemoUser);

    // No equipment filter — any equipment.

    if (params.loadType === 'full') {
      query = query.eq('load_type', 'FTL');
    } else if (params.loadType === 'partial') {
      query = query.in('load_type', ['LTL', 'Partial'] as any);
    }

    if (params.hideCanada) {
      query = query.neq('origin_country', 'Canada').neq('destination_country', 'Canada');
    }

    query = query
      .eq('origin_state', originStateCode)
      .eq('destination_state', destStateCode);

    const { data, error } = await query;
    if (error) {
      console.error('Error in searchStateToStateAnyEquipment:', error);
      return [];
    }

    const results = (data || []) as DatabaseLoad[];
    const filtered = results.filter(l => !excludeIds.has(l.id));
    return filtered;
  } catch (error) {
    console.error('Error in searchStateToStateAnyEquipment:', error);
    return [];
  }
};

export function matchesSearchFilters(
  load: DatabaseLoad,
  params: LoadSearchParams,
  originCoords: GeoCoords | null,
  destCoords: GeoCoords | null,
): boolean {
  // Must be public + posted
  if (!load.is_public || load.status !== 'posted') return false;

  // Equipment type (with family expansion for base types)
  if (params.equipmentTypes.length > 0) {
    const expanded = expandEquipmentTypes(params.equipmentTypes.map(e => e.toLowerCase()));
    const validEq = expanded.filter(e => DB_EQUIPMENT_ENUM.has(e));
    if (validEq.length > 0 && !validEq.includes((load.equipment_type || '').toLowerCase())) return false;
  }

  // Load type
  if (params.loadType === 'full' && load.load_type !== 'FTL') return false;
  if (params.loadType === 'partial' && load.load_type !== 'LTL' && load.load_type !== 'Partial') return false;
  // 'both' → no load_type check

  // Hide Canada
  if (params.hideCanada) {
    if (load.origin_country === 'Canada' || load.destination_country === 'Canada') return false;
  }

  // Origin radius check
  if (originCoords && params.dhoRadius && params.dhoRadius > 0) {
    const lat = load.origin_latitude;
    const lon = load.origin_longitude;
    if (!lat || !lon) return false;
    if (haversineDistance(originCoords.lat, originCoords.lon, lat, lon) > params.dhoRadius) return false;
  } else if (originCoords === null && params.origin?.trim()) {
    // No coords but origin specified — city name match
    const originCity = params.origin.split(',')[0].trim().toLowerCase();
    if (!load.origin_city.toLowerCase().includes(originCity)) return false;
  }

  // Destination radius check
  if (destCoords && params.dhdRadius && params.dhdRadius > 0) {
    const lat = load.destination_latitude;
    const lon = load.destination_longitude;
    if (!lat || !lon) return false;
    if (haversineDistance(destCoords.lat, destCoords.lon, lat, lon) > params.dhdRadius) return false;
  } else if (destCoords === null && params.destination?.trim()) {
    const destCity = params.destination.split(',')[0].trim().toLowerCase();
    if (!load.destination_city.toLowerCase().includes(destCity)) return false;
  }

  // Pickup date expiration check
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (load.pickup_date < today.toISOString().split('T')[0]) return false;

  return true;
}

